/**
 * Locale-aware value parsers for database column type conversions.
 *
 * Supports US, German/European, British, and ISO formats for both
 * numbers and dates. Used by converts.ts when changing column types.
 */

// ── Number Parsing ────────────────────────────────────────────────────

/**
 * Parse number text supporting US ("1,234.56") and German ("1.234,56") formats.
 * Returns null if unparseable.
 */
export function parseNumberText(text: string): number | null {
  let t = text.trim().replace(/[€$£¥₹%\s\u00A0]/g, '');
  if (!t) return null;

  let sign = 1;
  if (t.startsWith('-')) {
    sign = -1;
    t = t.slice(1);
  } else if (t.startsWith('+')) {
    t = t.slice(1);
  }

  if (/^\d+$/.test(t)) return sign * parseInt(t, 10);

  const lastDot = t.lastIndexOf('.');
  const lastComma = t.lastIndexOf(',');
  const hasDot = lastDot >= 0;
  const hasComma = lastComma >= 0;

  // Both present → last separator is decimal
  if (hasDot && hasComma) {
    if (lastDot > lastComma) {
      return toFloat(t.replace(/,/g, ''), sign);
    }
    return toFloat(t.replace(/\./g, '').replace(',', '.'), sign);
  }

  // Only comma
  if (hasComma && !hasDot) {
    if ((t.match(/,/g) || []).length > 1) {
      return toFloat(t.replace(/,/g, ''), sign);
    }
    const afterComma = t.slice(lastComma + 1);
    const beforeComma = t.slice(0, lastComma);
    if (
      afterComma.length === 3 &&
      /^\d+$/.test(afterComma) &&
      beforeComma.length >= 1
    ) {
      return toFloat(t.replace(/,/g, ''), sign);
    }
    return toFloat(t.replace(',', '.'), sign);
  }

  // Only dot
  if (hasDot && !hasComma) {
    if ((t.match(/\./g) || []).length > 1) {
      return toFloat(t.replace(/\./g, ''), sign);
    }
    return toFloat(t, sign);
  }

  return null;
}

function toFloat(s: string, sign: number): number | null {
  const n = parseFloat(s);
  if (isNaN(n)) return null;
  return sign * n;
}

// ── Date Parsing ──────────────────────────────────────────────────────

const ENGLISH_MONTHS: Record<string, number> = {
  January: 1,
  February: 2,
  March: 3,
  April: 4,
  May: 5,
  June: 6,
  July: 7,
  August: 8,
  September: 9,
  October: 10,
  November: 11,
  December: 12,
};
const SHORT_MONTHS: Record<string, number> = {
  Jan: 1,
  Feb: 2,
  Mar: 3,
  Apr: 4,
  May: 5,
  Jun: 6,
  Jul: 7,
  Aug: 8,
  Sep: 9,
  Oct: 10,
  Nov: 11,
  Dec: 12,
};
const GERMAN_MONTHS: Record<string, number> = {
  Januar: 1,
  Februar: 2,
  März: 3,
  April: 4,
  Mai: 5,
  Juni: 6,
  Juli: 7,
  August: 8,
  September: 9,
  Oktober: 10,
  November: 11,
  Dezember: 12,
};

/**
 * Parse date text in various formats to UTC midnight timestamp (ms).
 * Returns null if unparseable.
 */
export function parseDateText(text: string): number | null {
  const t = text.trim();
  if (!t) return null;

  let m: RegExpMatchArray | null;

  // ISO: 2025-07-31
  m = t.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));

  // DD.MM.YYYY
  m = t.match(/^(\d{1,2})\.(\d{2})\.(\d{4})$/);
  if (m) return Date.UTC(Number(m[3]), Number(m[2]) - 1, Number(m[1]));

  // MM/DD/YYYY or MM-DD-YYYY
  m = t.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (m) return Date.UTC(Number(m[3]), Number(m[1]) - 1, Number(m[2]));

  // German: "8. Juni 2025"
  m = t.match(/^(\d{1,2})\.\s*([A-Za-zÀ-ÿ]+)\s+(\d{4})$/);
  if (m && m[2]) {
    const mo = GERMAN_MONTHS[m[2]];
    if (mo) return Date.UTC(Number(m[3]), mo - 1, Number(m[1]));
  }

  // English: "July 31, 2025" / "Sep 30, 2025"
  m = t.match(/^([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})$/);
  if (m && m[1]) {
    const mo = ENGLISH_MONTHS[m[1]] ?? SHORT_MONTHS[m[1]];
    if (mo) return Date.UTC(Number(m[3]), mo - 1, Number(m[2]));
  }

  // British: "31 July 2025"
  m = t.match(/^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})$/);
  if (m && m[2]) {
    const mo = ENGLISH_MONTHS[m[2]] ?? SHORT_MONTHS[m[2]];
    if (mo) return Date.UTC(Number(m[3]), mo - 1, Number(m[1]));
  }

  return null;
}
